/**
 * Health scoring utilities for weekly check-in responses.
 * Used by the Leadership Snapshot cards to classify risk levels
 * and surface actionable badges.
 */

// ---------------------------------------------------------------------------
// Rating helpers (reusable across components)
// ---------------------------------------------------------------------------

/** Badge tone for a 1-5 rating. */
export function getRatingTone(rating) {
  if (rating >= 4) return "success";
  if (rating >= 3) return "warning";
  return "danger";
}

const TONE_TEXT = {
  success: "text-success-fg",
  warning: "text-warning-fg",
  danger: "text-danger-fg",
};

/** Token text-color class for a 1-5 rating. */
export function getRatingTextClass(rating) {
  return TONE_TEXT[getRatingTone(rating)];
}

const HEALTH_LEVEL_TONES = {
  healthy: "success",
  monitor: "warning",
  elevated: "danger",
};

/** Badge tone for a health level ("healthy" | "monitor" | "elevated"). */
export function getHealthLevelTone(healthLevel) {
  return HEALTH_LEVEL_TONES[healthLevel] || "neutral";
}

/** Human-friendly label for a 1-5 rating */
export function getRatingLabel(rating) {
  if (rating >= 5) return "Excellent";
  if (rating >= 4) return "Good";
  if (rating >= 3) return "Okay";
  if (rating >= 2) return "Low";
  return "Very Low";
}

/** Returns true if the response contains a meaningful blocker */
export function hasBlocker(response) {
  const b = response?.blockers;
  if (!b || typeof b !== "string") return false;
  const normalized = b.trim().toLowerCase();
  return normalized.length > 0 && normalized !== "none" && normalized !== "n/a";
}

// ---------------------------------------------------------------------------
// Health level + flag computation
// ---------------------------------------------------------------------------

/**
 * Compute health level, flags, and a ready-to-render badge for a response.
 *
 * @param {Object}   response            - Current week response
 * @param {Object[]} priorWeekResponses  - Same person's responses from previous weeks
 *                                         (ordered newest-first). Optional.
 * @returns {{ healthLevel, flags, badge }}
 */
export function getResponseHealth(response, priorWeekResponses = []) {
  const satisfaction = response?.roleSatisfaction || 0;
  const contribution = response?.contributionScore || 0;
  const blocker = hasBlocker(response);

  // --- Health level ---
  // elevated: any critical score or active blocker
  // monitor:  borderline scores worth watching
  // healthy:  all clear
  let healthLevel;
  if (satisfaction <= 2 || contribution <= 2 || blocker) {
    healthLevel = "elevated";
  } else if (satisfaction === 3 || contribution === 3) {
    healthLevel = "monitor";
  } else {
    healthLevel = "healthy";
  }

  // --- Flags ---
  const flags = [];

  if (blocker) flags.push("blocker");

  // burnoutRisk: elevated 2+ consecutive weeks, OR satisfaction drops 2+ WoW,
  // OR (blocker present AND satisfaction <= 3)
  if (blocker && satisfaction <= 3) {
    flags.push("burnoutRisk");
  } else if (priorWeekResponses.length > 0) {
    const prev = priorWeekResponses[0]; // most recent prior week
    const prevSatisfaction = prev?.roleSatisfaction || 0;

    // Satisfaction drop of 2+ points week-over-week
    if (prevSatisfaction > 0 && satisfaction > 0 && prevSatisfaction - satisfaction >= 2) {
      flags.push("burnoutRisk");
    }

    // Elevated 2+ consecutive weeks
    if (healthLevel === "elevated") {
      const prevHealth = getResponseHealth(prev); // no recursion beyond 1 level
      if (prevHealth.healthLevel === "elevated") {
        if (!flags.includes("burnoutRisk")) flags.push("burnoutRisk");
      }
    }
  }

  // followUp: monitor level, or has story/highlight worth reading
  if (healthLevel === "monitor" || response?.story) {
    flags.push("followUp");
  }

  // --- Badge ---
  const badge = computeBadge(healthLevel, flags);

  return { healthLevel, flags, badge };
}

// ---------------------------------------------------------------------------
// Badge mapping
// ---------------------------------------------------------------------------

function computeBadge(healthLevel, flags) {
  if (healthLevel === "elevated" && flags.includes("burnoutRisk")) {
    return { label: "Burnout risk", tone: "danger" };
  }
  if (healthLevel === "elevated" && flags.includes("blocker")) {
    return { label: "Blocker: support needed", tone: "warning" };
  }
  if (healthLevel === "elevated") {
    return { label: "Needs attention", tone: "danger" };
  }
  if (healthLevel === "monitor" && flags.includes("followUp")) {
    return { label: "Follow-up recommended", tone: "warning" };
  }
  if (healthLevel === "monitor") {
    return { label: "Monitor", tone: "warning" };
  }
  return { label: "Doing well", tone: "success" };
}
